import React from "react";

// Simple oblique projection of a garage. All shapes update smoothly via CSS transitions.
// Inputs:
//  width:  3 .. 12  m  (front horizontal)
//  length: 4 .. 20  m  (depth)
//  height: 2.3..4.5 m  (vertical)
//  color:  hex string
//  door:   "billeno" | "szekcionalt" | "automata" | "kettos"
//  roof:   "lapos" | "nyereg" | "pult"
//  step:   1..4  — controls which parts are drawn (sketch -> color -> door -> roof)

export default function GarageVisualizer({
  width = 4,
  length = 6,
  height = 2.8,
  color = "#3A3F44",
  trimColor = "#0F0F0F",
  door = "billeno",
  roof = "pult",
  step = 1,
}) {
  // Map physical dimensions to SVG units
  const W = 60 + (width - 3) * 22;       // 60..258
  const H = 80 + (height - 2.3) * 35;    // 80..157
  const D = 20 + (length - 4) * 9;       // 20..164  (depth)

  // Origin of the front-bottom-left corner
  const x0 = 100;
  const y0 = 280;

  // Depth offset (oblique 30°-ish)
  const dx = D * 0.55;
  const dy = -D * 0.4;

  // Front face corners
  const fbl = [x0, y0];
  const fbr = [x0 + W, y0];
  const ftr = [x0 + W, y0 - H];
  const ftl = [x0, y0 - H];

  // Back face corners (offset by depth)
  const bbl = [fbl[0] + dx, fbl[1] + dy];
  const bbr = [fbr[0] + dx, fbr[1] + dy];
  const btr = [ftr[0] + dx, ftr[1] + dy];
  const btl = [ftl[0] + dx, ftl[1] + dy];

  // Roof variations (replaces top of box)
  // Peak point for nyereg (middle of front + depth offset for ridge)
  const peakHeight = roof === "nyereg" ? 28 : 0;
  const ftlR = [ftl[0], ftl[1]];
  const ftrR = [ftr[0], ftr[1]];
  const btlR = [btl[0], btl[1]];
  const btrR = [btr[0], btr[1]];

  // Front face polygon (walls)
  let frontPolygon;
  let sidePolygon;
  let roofPolygon;
  let roofRidge = null;

  if (roof === "nyereg") {
    // Front gable: trapezoid with peak in middle
    const pkX = (ftl[0] + ftr[0]) / 2;
    const pkY = ftl[1] - peakHeight;
    const pkBX = pkX + dx;
    const pkBY = pkY + dy;
    frontPolygon = `${fbl[0]},${fbl[1]} ${fbr[0]},${fbr[1]} ${ftr[0]},${ftr[1]} ${pkX},${pkY} ${ftl[0]},${ftl[1]}`;
    // Right side wall (visible)
    sidePolygon = `${fbr[0]},${fbr[1]} ${bbr[0]},${bbr[1]} ${btr[0]},${btr[1]} ${ftr[0]},${ftr[1]}`;
    // Roof: two slopes
    roofPolygon = `${ftl[0]},${ftl[1]} ${pkX},${pkY} ${pkBX},${pkBY} ${btl[0]},${btl[1]}`;
    roofRidge = { x1: pkX, y1: pkY, x2: pkBX, y2: pkBY };
    // Right roof slope
    var roofPolygon2 = `${ftr[0]},${ftr[1]} ${pkX},${pkY} ${pkBX},${pkBY} ${btr[0]},${btr[1]}`;
  } else if (roof === "pult") {
    // Single slope: front wall taller, back lower (shed roof)
    const drop = 22;
    const ftlS = [ftl[0], ftl[1]];                 // front top stays
    const ftrS = [ftr[0], ftr[1]];
    const btlS = [btl[0], btl[1] + drop];          // back goes down
    const btrS = [btr[0], btr[1] + drop];
    frontPolygon = `${fbl[0]},${fbl[1]} ${fbr[0]},${fbr[1]} ${ftrS[0]},${ftrS[1]} ${ftlS[0]},${ftlS[1]}`;
    sidePolygon = `${fbr[0]},${fbr[1]} ${bbr[0]},${bbr[1]} ${btrS[0]},${btrS[1]} ${ftrS[0]},${ftrS[1]}`;
    roofPolygon = `${ftlS[0]},${ftlS[1]} ${ftrS[0]},${ftrS[1]} ${btrS[0]},${btrS[1]} ${btlS[0]},${btlS[1]}`;
  } else {
    // Pult (shed) as default fallback — single slope
    const drop = 22;
    const ftlS = [ftl[0], ftl[1]];
    const ftrS = [ftr[0], ftr[1]];
    const btlS = [btl[0], btl[1] + drop];
    const btrS = [btr[0], btr[1] + drop];
    frontPolygon = `${fbl[0]},${fbl[1]} ${fbr[0]},${fbr[1]} ${ftrS[0]},${ftrS[1]} ${ftlS[0]},${ftlS[1]}`;
    sidePolygon = `${fbr[0]},${fbr[1]} ${bbr[0]},${bbr[1]} ${btrS[0]},${btrS[1]} ${ftrS[0]},${ftrS[1]}`;
    roofPolygon = `${ftlS[0]},${ftlS[1]} ${ftrS[0]},${ftrS[1]} ${btrS[0]},${btrS[1]} ${btlS[0]},${btlS[1]}`;
  }

  // Color visibility — only after step 2
  const wallFill = step >= 2 ? color : "transparent";
  const wallStroke = step >= 2 ? trimColor : "#0F0F0F";
  const sideShade = step >= 2 ? darken(color, 0.18) : "transparent";
  const roofShade = step >= 4 ? darken(color, 0.32) : "transparent";

  // Door geometry on front face
  const doorVisible = step >= 3;
  const doorH = Math.min(H * 0.78, 110);
  const doorW = Math.min(W * 0.48, 110);
  const doorX = fbl[0] + (W - doorW) / 2;
  const doorY = y0 - doorH;

  return (
    <svg
      data-testid="garage-visualizer"
      viewBox="0 0 400 320"
      className="w-full h-full"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#E4E4E7" strokeWidth="0.5" />
        </pattern>
        <pattern id="sectional" width="10" height="14" patternUnits="userSpaceOnUse">
          <line x1="0" y1="0" x2="14" y2="0" stroke="#0F0F0F" strokeWidth="0.6" />
        </pattern>
      </defs>

      {/* Blueprint grid background */}
      <rect width="400" height="320" fill="url(#grid)" />

      {/* Ground line */}
      <line x1="20" y1={y0} x2="380" y2={y0} stroke="#0F0F0F" strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
      <line x1={bbl[0] - 20} y1={bbl[1]} x2={bbr[0] + 20} y2={bbr[1]} stroke="#0F0F0F" strokeWidth="0.5" strokeDasharray="2 3" opacity="0.3" />

      {/* Side wall (back face) */}
      <polygon
        className="garage-part"
        points={sidePolygon}
        fill={sideShade}
        stroke={wallStroke}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />

      {/* Roof */}
      {step >= 1 && (
        <polygon
          className="garage-part"
          points={roofPolygon}
          fill={roofShade}
          stroke={wallStroke}
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      )}
      {roof === "nyereg" && step >= 1 && roofPolygon2 && (
        <polygon
          className="garage-part"
          points={roofPolygon2}
          fill={roofShade}
          stroke={wallStroke}
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      )}
      {roofRidge && step >= 4 && (
        <line
          className="garage-part"
          x1={roofRidge.x1}
          y1={roofRidge.y1}
          x2={roofRidge.x2}
          y2={roofRidge.y2}
          stroke="#0F0F0F"
          strokeWidth="1.6"
        />
      )}

      {/* Front wall */}
      <polygon
        className="garage-part"
        points={frontPolygon}
        fill={wallFill}
        stroke={wallStroke}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />

      {/* Door */}
      {doorVisible && <Door door={door} x={doorX} y={doorY} w={doorW} h={doorH} />}

      {/* Window (small, on side wall) */}
      {step >= 3 && roof !== "nyereg" && (
        <rect
          className="garage-part"
          x={fbr[0] + dx * 0.45 - 6}
          y={ftr[1] + dy * 0.45 + 18}
          width="14"
          height="14"
          fill="#FFFFFF"
          stroke={wallStroke}
          strokeWidth="1"
        />
      )}

      {/* Dimension lines */}
      {step >= 1 && (
        <>
          {/* Width */}
          <g opacity="0.55">
            <line x1={fbl[0]} y1={y0 + 18} x2={fbr[0]} y2={y0 + 18} stroke="#0F0F0F" strokeWidth="0.6" />
            <line x1={fbl[0]} y1={y0 + 14} x2={fbl[0]} y2={y0 + 22} stroke="#0F0F0F" strokeWidth="0.6" />
            <line x1={fbr[0]} y1={y0 + 14} x2={fbr[0]} y2={y0 + 22} stroke="#0F0F0F" strokeWidth="0.6" />
            <text x={(fbl[0] + fbr[0]) / 2} y={y0 + 32} textAnchor="middle" fontSize="10" fontFamily="Manrope" fill="#0F0F0F">
              {width.toFixed(1)} m
            </text>
          </g>
          {/* Height */}
          <g opacity="0.55">
            <line x1={fbl[0] - 14} y1={ftl[1]} x2={fbl[0] - 14} y2={fbl[1]} stroke="#0F0F0F" strokeWidth="0.6" />
            <line x1={fbl[0] - 18} y1={ftl[1]} x2={fbl[0] - 10} y2={ftl[1]} stroke="#0F0F0F" strokeWidth="0.6" />
            <line x1={fbl[0] - 18} y1={fbl[1]} x2={fbl[0] - 10} y2={fbl[1]} stroke="#0F0F0F" strokeWidth="0.6" />
            <text x={fbl[0] - 22} y={(ftl[1] + fbl[1]) / 2} textAnchor="end" fontSize="10" fontFamily="Manrope" fill="#0F0F0F">
              {height.toFixed(1)} m
            </text>
          </g>
          {/* Depth */}
          <g opacity="0.55">
            <line x1={fbr[0] + 8} y1={fbr[1] + 6} x2={bbr[0] + 8} y2={bbr[1] + 6} stroke="#0F0F0F" strokeWidth="0.6" />
            <text x={fbr[0] + dx / 2 + 18} y={fbr[1] + dy / 2 + 6} fontSize="10" fontFamily="Manrope" fill="#0F0F0F">
              {length.toFixed(1)} m
            </text>
          </g>
        </>
      )}
    </svg>
  );
}

function Door({ door, x, y, w, h }) {
  if (door === "kettos") {
    const halfW = w / 2 - 1;
    return (
      <g className="garage-part">
        <rect x={x} y={y} width={halfW} height={h} fill="#FFFFFF" stroke="#0F0F0F" strokeWidth="1.2" />
        <rect x={x + halfW + 2} y={y} width={halfW} height={h} fill="#FFFFFF" stroke="#0F0F0F" strokeWidth="1.2" />
        <circle cx={x + halfW - 4} cy={y + h / 2} r="1.5" fill="#0F0F0F" />
        <circle cx={x + halfW + 6} cy={y + h / 2} r="1.5" fill="#0F0F0F" />
      </g>
    );
  }
  // Sectional and automata: paneled door
  if (door === "szekcionalt" || door === "automata") {
    const panels = 5;
    const panelH = h / panels;
    return (
      <g className="garage-part">
        <rect x={x} y={y} width={w} height={h} fill="#FFFFFF" stroke="#0F0F0F" strokeWidth="1.4" />
        {Array.from({ length: panels - 1 }).map((_, i) => (
          <line
            key={i}
            x1={x}
            y1={y + (i + 1) * panelH}
            x2={x + w}
            y2={y + (i + 1) * panelH}
            stroke="#0F0F0F"
            strokeWidth="0.8"
            opacity="0.6"
          />
        ))}
        {door === "automata" && (
          <>
            <rect x={x + w - 14} y={y + h - 14} width="10" height="10" fill="#FFFFFF" stroke="#0F0F0F" strokeWidth="0.8" />
            <text x={x + w - 9} y={y + h - 6} textAnchor="middle" fontSize="7" fontFamily="Manrope" fill="#0F0F0F" fontWeight="700">A</text>
          </>
        )}
      </g>
    );
  }
  // Billenő (single panel tilt-up)
  return (
    <g className="garage-part">
      <rect x={x} y={y} width={w} height={h} fill="#FFFFFF" stroke="#0F0F0F" strokeWidth="1.4" />
      <line x1={x} y1={y + h * 0.5} x2={x + w} y2={y + h * 0.5} stroke="#0F0F0F" strokeWidth="0.6" opacity="0.4" />
      <circle cx={x + w / 2} cy={y + h * 0.6} r="2" fill="#0F0F0F" />
    </g>
  );
}

function darken(hex, amount = 0.2) {
  const c = hex.replace("#", "");
  const num = parseInt(c, 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  r = Math.max(0, Math.floor(r * (1 - amount)));
  g = Math.max(0, Math.floor(g * (1 - amount)));
  b = Math.max(0, Math.floor(b * (1 - amount)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}
